import { v4 as uuid } from 'uuid';

export const blankAddress = {
  address: '',
  address_2: '',
  city: '',
  state: '',
  zip_postal_code: '',
  country: 'US',
}

export const blankIndividual = {
  first_name: '',
  middle_name: '',
  last_name: '',
  phone: '',
  date_of_birth: '',
  ssn: '',
  document_type: 'passport',
  id_document: '',
  id_document_secondary: '',
  persona_account_id: '',
  persona_inquiry_id: '',
  persona_inquiry_status: '',
  address: blankAddress,
}

export const blankRepresentative = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
}

export const blankOwner = {
  percentage_ownership: '',
  email: '',
  individual: blankIndividual,
}

export const blankEntity = {
  name: '',
  company_url: '',
  kind: 'corporation',
  certificate_of_good_standing: '',
  organization_documents: '',
  aml_policy: '',
  legal_entity_identifier: '',
  ein: '',
  address: blankAddress,
  authorized_representatives: [blankRepresentative, blankRepresentative],
  beneficial_owners: [],
}

export const blankApplication = {
  email: '',
  referral_source: '',
  referral_source_other: '',
  account_type: 'individual',
  ecp_status: '',
  status: 'created',
  registration_revoked: null,
  convicted_felon: null,
  misdemeanor: null,
  under_investigation: null,
  background_details: '',
  can_be_ecp: false,
  agree: false,
  signature: '',
}

export const makeAddress = (initialAddress) => {
  const address = initialAddress ? {
    address: initialAddress.address || blankAddress.address,
    address_2: initialAddress.address_2 || blankAddress.address_2,
    city: initialAddress.city || blankAddress.city,
    state: initialAddress.state || blankAddress.state,
    zip_postal_code: initialAddress.zip_postal_code || blankAddress.zip_postal_code,
    country: initialAddress.country || blankAddress.country,
  } : makeAddress(blankAddress);

  return address;
}

export const makeIndividual = (initialIndividual) => {
  const individual = initialIndividual ? {
    first_name: initialIndividual.first_name || blankIndividual.first_name,
    middle_name: initialIndividual.middle_name || blankIndividual.middle_name,
    last_name: initialIndividual.last_name || blankIndividual.last_name,
    phone: initialIndividual.phone || blankIndividual.phone,
    date_of_birth: initialIndividual.date_of_birth || blankIndividual.date_of_birth,
    ssn: initialIndividual.ssn || blankIndividual.ssn,
    document_type: initialIndividual.document_type || blankIndividual.document_type,
    id_document: initialIndividual.id_document || blankIndividual.id_document,
    id_document_secondary: initialIndividual.id_document_secondary || blankIndividual.id_document_secondary,
    persona_account_id: initialIndividual.persona_account_id || blankIndividual.persona_account_id,
    persona_inquiry_id: blankIndividual.persona_inquiry_id, // initial value is not fetched from server
    persona_inquiry_status: initialIndividual.persona_inquiry_status || blankIndividual.persona_inquiry_status,
    address: makeAddress(initialIndividual.address),
  } : makeIndividual(blankIndividual);

  return individual;
}

export const makeRepresentative = (initialRepresentative) => {
  const representative = initialRepresentative ? {
    first_name: initialRepresentative.first_name || blankRepresentative.first_name,
    last_name: initialRepresentative.last_name || blankRepresentative.last_name,
    email: initialRepresentative.email || blankRepresentative.email,
    phone: initialRepresentative.phone || blankRepresentative.phone,
  } : makeRepresentative(blankRepresentative);

  return representative;
}

export const makeAuthorizedRepresentatives = (initialRepresentatives) => {
  const representatives = (initialRepresentatives && initialRepresentatives.length) ?
    initialRepresentatives.map(rep => makeRepresentative(rep)) :
    [blankRepresentative, blankRepresentative];

  // there must always be two representatives for validation to work as expected
  if (representatives.length < 2) representatives.push(blankRepresentative);

  return representatives;
}

export const makeOwner = (initialOwner) => {
  const owner = initialOwner ? {
    key: initialOwner.key || uuid(),
    percentage_ownership: initialOwner.percentage_ownership || blankOwner.percentage_ownership,
    email: initialOwner.email || blankOwner.email,
    individual: makeIndividual(initialOwner.individual),
  } : makeOwner(blankOwner);

  return owner;
}

export const makeBeneficialOwners = (initialOwners) => {
  const owners = initialOwners ? initialOwners.map(owner => makeOwner(owner)) : [];

  return owners;
}

export const makeEntity = (initialEntity) => {
  const entity = initialEntity ? {
    name: initialEntity.name || blankEntity.name,
    company_url: initialEntity.company_url || blankEntity.company_url,
    kind: initialEntity.kind || blankEntity.kind,
    certificate_of_good_standing: initialEntity.certificate_of_good_standing || blankEntity.certificate_of_good_standing,
    organization_documents: initialEntity.organization_documents || blankEntity.organization_documents,
    aml_policy: initialEntity.aml_policy || blankEntity.aml_policy,
    legal_entity_identifier: initialEntity.legal_entity_identifier || blankEntity.legal_entity_identifier,
    ein: initialEntity.ein || blankEntity.ein,
    address: makeAddress(initialEntity.address),
    authorized_representatives: makeAuthorizedRepresentatives(initialEntity.authorized_representatives),
    beneficial_owners: makeBeneficialOwners(initialEntity.beneficial_owners),
  } : makeEntity(blankEntity);

  return entity;
}

export const makeApplication = (initialApplication) => {
  const application = initialApplication ? {
    individual: makeIndividual(initialApplication.individual),
    entity: makeEntity(initialApplication.entity),
    email: initialApplication.email || blankApplication.email,
    referral_source: initialApplication.referral_source || blankApplication.referral_source,
    referral_source_other: initialApplication.referral_source_other || blankApplication.referral_source_other,
    account_type: initialApplication.account_type || blankApplication.account_type,
    ecp_status: initialApplication.ecp_status || blankApplication.ecp_status,
    status: initialApplication.status || blankApplication.status,
    registration_revoked: initialApplication.registration_revoked || blankApplication.registration_revoked,
    convicted_felon: initialApplication.convicted_felon || blankApplication.convicted_felon,
    misdemeanor: initialApplication.misdemeanor || blankApplication.misdemeanor,
    under_investigation: initialApplication.under_investigation || blankApplication.under_investigation,
    background_details: initialApplication.background_details || blankApplication.background_details,
    can_be_ecp: initialApplication.can_be_ecp || blankApplication.can_be_ecp,
    agree: blankApplication.agree,
    signature: blankApplication.signature,
  } : makeApplication(blankApplication);

  return application;
}
